const AU_STATE_TIMEZONE_MAP: Record<string, string> = {
    NSW: "Australia/Sydney",
    VIC: "Australia/Melbourne",
    QLD: "Australia/Brisbane",
    WA: "Australia/Perth",
    SA: "Australia/Adelaide",
    TAS: "Australia/Hobart",
    ACT: "Australia/Sydney",
    NT: "Australia/Darwin",
};

const DEFAULT_AU_TIMEZONE = "Australia/Sydney";
const AU_LOCALE = "en-AU";

function partsToMap(parts: Intl.DateTimeFormatPart[]): Record<string, string> {
    const map: Record<string, string> = {};
    for (const part of parts) {
        if (part.type !== "literal") {
            map[part.type] = part.value;
        }
    }
    return map;
}

function getTimezoneForState(state?: string | null): string {
    if (!state) {
        return DEFAULT_AU_TIMEZONE;
    }

    return AU_STATE_TIMEZONE_MAP[state.trim().toUpperCase()] ?? DEFAULT_AU_TIMEZONE;
}

/** YYYY-MM-DD for an instant in the given IANA timezone (en-AU parts). */
function formatDateKey(date: Date | string | number, timeZone: string): string {
    const d = date instanceof Date ? date : new Date(date);
    const map = partsToMap(
        new Intl.DateTimeFormat(AU_LOCALE, {
            timeZone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).formatToParts(d),
    );
    return `${map.year}-${map.month}-${map.day}`;
}

/** Long-form AU date label from a YYYY-MM-DD key in the given timezone. */
function formatDateLabel(dateKey: string, timeZone: string): string {
    const [y, m, d] = dateKey.split("-").map(Number);
    const date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    return date.toLocaleDateString(AU_LOCALE, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone,
    });
}

/**
 * Converts a wall-clock date/time as observed in `timeZone` into the
 * corresponding UTC instant.
 */
function zonedTimeToUtc(
    wallTime: { year: number; month: number; day: number; hour: number; minute: number; second?: number },
    timeZone: string,
): Date {
    const { year, month, day, hour, minute, second = 0 } = wallTime;
    const targetUtc = Date.UTC(year, month - 1, day, hour, minute, second);
    let utcGuess = targetUtc;

    const formatter = new Intl.DateTimeFormat(AU_LOCALE, {
        timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });

    for (let i = 0; i < 2; i++) {
        const map = partsToMap(formatter.formatToParts(new Date(utcGuess)));

        const asIfUtc = Date.UTC(
            Number(map.year),
            Number(map.month) - 1,
            Number(map.day),
            Number(map.hour) === 24 ? 0 : Number(map.hour),
            Number(map.minute),
            Number(map.second),
        );

        utcGuess += targetUtc - asIfUtc;
    }

    return new Date(utcGuess);
}

/**
 * Calendar Y-M-D from a date-picker `Date`.
 *
 * Date pickers encode the selected day in local `getFullYear` / `getMonth` /
 * `getDate`. Pass picker dates (or `new Date(y, m - 1, d)` for an intentional
 * wall day) — not arbitrary UTC/ISO instants.
 */
function getWallDateParts(date: Date): { year: number; month: number; day: number } {
    return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
    };
}

/** Start/end of a calendar day at the property, as ISO strings for API queries.
 *  `date` must be a picker-style Date (local Y-M-D = intended property wall day). */
function getDayBoundsIso(date: Date, timeZone: string): { startTime: string; endTime: string } {
    const { year, month, day } = getWallDateParts(date);
    const start = zonedTimeToUtc({ year, month, day, hour: 0, minute: 0, second: 0 }, timeZone);
    const end = zonedTimeToUtc({ year, month, day, hour: 23, minute: 59, second: 59 }, timeZone);
    return { startTime: start.toISOString(), endTime: end.toISOString() };
}

/** Today's calendar date at the property timezone. */
function getTodayInTimezone(timeZone: string): { year: number; month: number; day: number } {
    const map = partsToMap(
        new Intl.DateTimeFormat(AU_LOCALE, {
            timeZone,
            year: "numeric",
            month: "numeric",
            day: "numeric",
        }).formatToParts(new Date()),
    );
    return {
        year: Number(map.year),
        month: Number(map.month),
        day: Number(map.day),
    };
}

function getTodayBoundsIso(timeZone: string): { startTime: string; endTime: string } {
    const { year, month, day } = getTodayInTimezone(timeZone);
    const start = zonedTimeToUtc({ year, month, day, hour: 0, minute: 0, second: 0 }, timeZone);
    const end = zonedTimeToUtc({ year, month, day, hour: 23, minute: 59, second: 59 }, timeZone);
    return { startTime: start.toISOString(), endTime: end.toISOString() };
}

export const auStateTimezoneUtils = {
    getTimezoneForState,
    formatDateKey,
    formatDateLabel,
    zonedTimeToUtc,
    getWallDateParts,
    getDayBoundsIso,
    getTodayBoundsIso,
    getTodayInTimezone,
};
